/**
 * Chatwoot Inboxes Service
 *
 * Handles all inbox (channel) related endpoints.
 * Account context is determined from JWT Bearer token.
 *
 * In Chatwoot, "inboxes" are channels like Email, WhatsApp, Facebook, etc.
 */

import { BaseChatwootService } from "../core/base-chatwoot-service";
import {
  InboxListResponse,
  InboxDetailResponse,
  ChatwootInbox,
} from "../types/inboxes.types";

/**
 * ChatwootInboxesService
 * Handles inbox-related API operations
 *
 * @example
 * ```typescript
 * const inboxesService = new ChatwootInboxesService();
 *
 * // List all inboxes (channels)
 * const inboxes = await inboxesService.listAllInboxes();
 *
 * // Get specific inbox details
 * const inbox = await inboxesService.getInbox(accountId, inboxId);
 * ```
 */
export class ChatwootInboxesService extends BaseChatwootService {
  constructor(baseUrl?: string) {
    super(baseUrl);
  }

  /**
   * List All Inboxes (Channels)
   * GET /api/accounts/inboxes
   *
   * Returns all inboxes (channels) for the current account.
   * In Chatwoot, inboxes represent different communication channels
   * like Email, WhatsApp, Facebook, Web Widget, etc.
   *
   * @returns Promise with inbox list
   *
   * @example
   * ```typescript
   * const inboxes = await service.listAllInboxes();
   * console.log(`Found ${inboxes.payload.length} inboxes`);
   * inboxes.payload.forEach(inbox => {
   *   console.log(`- ${inbox.name} (${inbox.channel_type})`);
   * });
   * ```
   */
  async listAllInboxes(): Promise<InboxListResponse> {
    // Use /api/accounts/inboxes (without /v1)
    return await this.get<InboxListResponse>("/api/accounts/inboxes");
  }

  /**
   * Get Inbox Details
   * GET /api/accounts/inboxes/{inboxId}
   *
   * Returns detailed information about a specific inbox.
   *
   * @param accountId - The numeric ID of the account (not used in URL, kept for compatibility)
   * @param inboxId - The numeric ID of the inbox
   * @returns Promise with inbox details
   *
   * @example
   * ```typescript
   * const inbox = await service.getInbox(1, 2);
   * console.log(inbox.payload.name, inbox.payload.channel_type);
   * ```
   */
  async getInbox(accountId: number, inboxId: number): Promise<InboxDetailResponse> {
    return await this.get<InboxDetailResponse>(`/api/accounts/inboxes/${inboxId}`);
  }

  /**
   * Update Inbox Settings
   * PATCH /api/accounts/inboxes/{inboxId}
   *
   * Update inbox configuration including name, features, etc.
   * 
   * Note: GoMake always disables channel greeting. The UI layer should always
   * pass greeting_enabled: false and greeting_message: "" when updating inbox settings.
   *
   * @param accountId - The numeric ID of the account (not used in URL, kept for compatibility)
   * @param inboxId - The numeric ID of the inbox
   * @param updates - Partial inbox settings to update
   * @returns Promise with updated inbox
   */
  async updateInbox(
    accountId: number,
    inboxId: number | string,
    updates: Partial<ChatwootInbox>
  ): Promise<InboxDetailResponse> {
    return await this.patch<InboxDetailResponse>(
      `/api/accounts/inboxes/${encodeURIComponent(String(inboxId))}`,
      updates
    );
  }

  /**
   * GET /api/accounts/inboxes/{inboxId}/signature
   * Returns a normalized signature config (empty defaults when not configured).
   */
  async getInboxSignature(
    accountId: number,
    inboxId: number | string
  ): Promise<import("../types/inbox-signature.types").InboxSignature> {
    return await this.get(
      `/api/accounts/inboxes/${encodeURIComponent(String(inboxId))}/signature`
    );
  }

  /**
   * PUT /api/accounts/inboxes/{inboxId}/signature
   */
  async updateInboxSignature(
    accountId: number,
    inboxId: number | string,
    payload: import("../types/inbox-signature.types").UpdateInboxSignaturePayload
  ): Promise<import("../types/inbox-signature.types").InboxSignature> {
    return await this.put(
      `/api/accounts/inboxes/${encodeURIComponent(String(inboxId))}/signature`,
      { signature: payload }
    );
  }

  /**
   * POST /api/accounts/inboxes/{inboxId}/signature/import-preview
   * Fetches + sanitizes Gmail signature. Does not persist.
   */
  async previewGmailSignatureImport(
    accountId: number,
    inboxId: number | string
  ): Promise<
    import("../types/inbox-signature.types").GmailSignatureImportPreview
  > {
    return await this.post(
      `/api/accounts/inboxes/${encodeURIComponent(String(inboxId))}/signature/import-preview`,
      {}
    );
  }

  /**
   * POST /api/accounts/inboxes/{inboxId}/signature/import
   * Re-fetches Gmail signature, sanitizes, and persists.
   */
  async confirmGmailSignatureImport(
    accountId: number,
    inboxId: number | string
  ): Promise<import("../types/inbox-signature.types").InboxSignature> {
    return await this.post(
      `/api/accounts/inboxes/${encodeURIComponent(String(inboxId))}/signature/import`,
      {}
    );
  }

  /**
   * Upload Inbox Avatar
   * POST /api/accounts/inboxes/{inboxId}/avatar
   *
   * Upload a new avatar image for the inbox.
   *
   * @param accountId - The numeric ID of the account (not used in URL, kept for compatibility)
   * @param inboxId - The numeric ID of the inbox
   * @param file - The image file to upload
   * @returns Promise with updated inbox
   */
  async uploadAvatar(
    accountId: number,
    inboxId: number,
    file: File
  ): Promise<InboxDetailResponse> {
    const formData = new FormData();
    formData.append("avatar", file);

    return await this.post<InboxDetailResponse>(
      `/api/accounts/inboxes/${inboxId}/avatar`,
      formData
    );
  }

  /**
   * Delete Inbox Avatar
   * DELETE /api/accounts/inboxes/{inboxId}/avatar
   *
   * Remove the current avatar image from the inbox.
   *
   * @param accountId - The numeric ID of the account (not used in URL, kept for compatibility)
   * @param inboxId - The numeric ID of the inbox
   * @returns Promise with updated inbox
   */
  async deleteAvatar(accountId: number, inboxId: number): Promise<InboxDetailResponse> {
    return await this.delete<InboxDetailResponse>(
      `/api/accounts/inboxes/${inboxId}/avatar`
    );
  }

  /**
   * Delete Inbox
   * DELETE /api/accounts/inboxes/{inboxId}
   *
   * Permanently delete an inbox and all its conversations.
   *
   * @param accountId - The numeric ID of the account (not used in URL, kept for compatibility)
   * @param inboxId - The numeric ID of the inbox
   * @returns Promise with void
   */
  async deleteInbox(accountId: number, inboxId: number): Promise<void> {
    return await this.delete<void>(`/api/accounts/inboxes/${inboxId}`);
  }

  /**
   * Update Inbox Agents (Add/Remove)
   * PATCH /api/accounts/inbox_members
   *
   * Update agents assigned to an inbox for handling conversations.
   * 
   * ⚠️ IMPORTANT: This is a REPLACEMENT operation, not additive.
   * All agents except the ones passed in the agentIds array will be REMOVED from the inbox.
   * 
   * Example:
   * - Current agents: [13, 14, 15]
   * - Call with agentIds: [13, 15]
   * - Result: Agent 14 is removed, only [13, 15] remain
   *
   * @see https://developers.chatwoot.com/api-reference/inboxes/update-agents-in-inbox
   * 
   * Note: Goes through messaging-hub-v2 proxy which handles account context via JWT.
   * Original Chatwoot endpoint: PATCH /api/v1/accounts/{account_id}/inbox_members
   * 
   * @param accountId - The numeric ID of the account (not used in URL, handled by proxy via JWT)
   * @param inboxId - The numeric ID of the inbox (sent in request body)
   * @param agentIds - Array of agent IDs to assign (REPLACES current assignments - agents not in this list will be removed)
   * @returns Promise with response containing updated agent list
   */
  async addAgentsToInbox(
    accountId: number,
    inboxId: number,
    agentIds: number[]
  ): Promise<any> {
    return await this.patch<any>(
      `/api/accounts/inbox_members`,
      {
        inbox_id: inboxId,
        user_ids: agentIds,
      }
    );
  }

  /**
   * Update Inbox Owners (co-owners)
   * PATCH /api/accounts/inbox_owners
   *
   * Gives collaborators the same rights as the inbox owner (edit, delete,
   * AI toggle, manage collaborators). REPLACEMENT operation: agents not in
   * agentIds lose owner rights. Only the primary owner may call this, and
   * every agent must already be a collaborator of the inbox.
   *
   * @param accountId - Account ID (not used in URL, kept for compatibility)
   * @param inboxId - The numeric ID of the inbox (sent in request body)
   * @param agentIds - Collaborator IDs that should be co-owners
   * @returns Promise with the updated collaborators list (with is_owner flags)
   */
  async updateInboxOwners(
    accountId: number,
    inboxId: number,
    agentIds: number[]
  ): Promise<any> {
    return await this.patch<any>(`/api/accounts/inbox_owners`, {
      inbox_id: inboxId,
      user_ids: agentIds,
    });
  }

  /**
   * Get Inbox Agents
   * GET /api/accounts/inbox_members/{inboxId}
   *
   * Get list of agents assigned to an inbox.
   *
   * @param accountId - The numeric ID of the account (not used in URL, kept for compatibility)
   * @param inboxId - The numeric ID of the inbox
   * @returns Promise with agents list
   */
  async getInboxAgents(accountId: number, inboxId: number): Promise<any> {
    return await this.get<any>(`/api/accounts/inbox_members/${inboxId}`);
  }

  /**
   * Update Business Hours
   * PATCH /api/accounts/inboxes/{inboxId}/business_hours
   *
   * Configure working hours for the inbox.
   *
   * @param accountId - The numeric ID of the account (not used in URL, kept for compatibility)
   * @param inboxId - The numeric ID of the inbox
   * @param schedule - Weekly schedule object with business hours
   * @returns Promise with updated inbox
   */
  async updateBusinessHours(
    accountId: number,
    inboxId: number,
    schedule: any
  ): Promise<InboxDetailResponse> {
    return await this.patch<InboxDetailResponse>(
      `/api/accounts/inboxes/${inboxId}/business_hours`,
      { business_hours: schedule }
    );
  }

  /**
   * Update CSAT Configuration
   * PATCH /api/accounts/inboxes/{inboxId}/csat
   *
   * Configure customer satisfaction survey settings.
   *
   * @param accountId - The numeric ID of the account (not used in URL, kept for compatibility)
   * @param inboxId - The numeric ID of the inbox
   * @param config - CSAT configuration object
   * @returns Promise with updated inbox
   */
  async updateCSATConfig(
    accountId: number,
    inboxId: number,
    config: any
  ): Promise<InboxDetailResponse> {
    return await this.patch<InboxDetailResponse>(
      `/api/accounts/inboxes/${inboxId}/csat`,
      { csat_config: config }
    );
  }

  /**
   * Sync WhatsApp Templates
   * POST /api/accounts/inboxes/{inboxId}/sync_templates
   *
   * Sync message templates from WhatsApp Business Account.
   *
   * @param accountId - The numeric ID of the account (not used in URL, kept for compatibility)
   * @param inboxId - The numeric ID of the inbox
   * @returns Promise with sync result
   */
  async syncWhatsAppTemplates(
    accountId: number,
    inboxId: number
  ): Promise<any> {
    return await this.post<any>(
      `/api/accounts/inboxes/${inboxId}/sync_templates`,
      {}
    );
  }

  /**
   * Manually sync a native email inbox (Gmail/Outlook).
   * POST /frontdesk/email/inboxes/{inboxId}/sync
   * inboxId may be Chatwoot-compat publicId or UUID.
   */
  async syncEmailInbox(inboxId: number | string): Promise<{
    inboxId: string;
    ingested: number;
    skipped: number;
    errors: number;
    mode: string;
  }> {
    return await this.post(
      `/frontdesk/email/inboxes/${inboxId}/sync`,
      {}
    );
  }

  /**
   * List approved WhatsApp templates from Meta via messaging-hub (native).
   * GET /api/accounts/inboxes/{inboxId}/whatsapp/templates
   */
  async listWhatsAppTemplates(
    inboxId: number | string
  ): Promise<{ data: Array<Record<string, unknown>> }> {
    return await this.get<{ data: Array<Record<string, unknown>> }>(
      `/api/accounts/inboxes/${encodeURIComponent(String(inboxId))}/whatsapp/templates`
    );
  }
}

